import { calendarNotes } from "./calendarNotes";
import { ACTION_TYPE_DEFAULTS } from "../data/sampleFeed";
import { hasNewsletterReceiptIdentity } from "./newsletterPresentation";


// ---------------------------------------------------------------------------
// Google Calendar delivery URL (2026-02, lightweight MVP path).
// Opens the parent's default calendar app with all fields prefilled.
// Works on iOS Safari / Chrome / Android / desktop without any Google
// or Outlook API integration. Falls back to an .ics data-URI when the
// event doesn't have a usable date.
// ---------------------------------------------------------------------------
const _pad = (n) => String(n).padStart(2, "0");

// Format a Date object as YYYYMMDDTHHMMSSZ (UTC, per Google spec).
const _fmtIcsUtc = (d) =>
  d.getUTCFullYear() +
  _pad(d.getUTCMonth() + 1) +
  _pad(d.getUTCDate()) +
  "T" +
  _pad(d.getUTCHours()) +
  _pad(d.getUTCMinutes()) +
  _pad(d.getUTCSeconds()) +
  "Z";

// Format a YYYY-MM-DD string as a Google Calendar all-day date pair.
const _fmtIcsDate = (ymd) => ymd.replace(/-/g, "");

// Build the Google Calendar create URL with title / dates / location /
// details. If the event has an `event_start` datetime, we pass a
// concrete UTC start + one-hour default end. If only `event_date` is
// present we use the all-day format (`YYYYMMDD/YYYYMMDD`).
export const buildGoogleCalendarUrl = (event) => {
  if (!event) return null;
  const title = event.headline || event.event_name || "School event";
  const parts = [];
  parts.push("action=TEMPLATE");
  parts.push("text=" + encodeURIComponent(title));
  let datesFragment = null;
  const _dateOnlyRe = /^\d{4}-\d{2}-\d{2}$/;
  const _allDayFragment = (startDate, endDate) => {
    // Google requires an EXCLUSIVE end date for all-day entries.
    const d = new Date((endDate || startDate) + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + 1);
    const end =
      d.getUTCFullYear() + _pad(d.getUTCMonth() + 1) + _pad(d.getUTCDate());
    return _fmtIcsDate(startDate) + "/" + end;
  };
  if (event.event_start && _dateOnlyRe.test(event.event_start)) {
    // Date-only start (possibly a date-only span): an ALL-DAY entry,
    // never a midnight appointment.
    datesFragment = _allDayFragment(
      event.event_start,
      _dateOnlyRe.test(event.event_end || "") ? event.event_end : null
    );
  } else if (event.event_start) {
    const start = new Date(event.event_start);
    if (!isNaN(start.getTime())) {
      const end = event.event_end
        ? new Date(event.event_end)
        : new Date(start.getTime() + 60 * 60 * 1000);
      if (!isNaN(end.getTime())) {
        datesFragment = _fmtIcsUtc(start) + "/" + _fmtIcsUtc(end);
      }
    }
  } else if (event.event_date && /^\d{4}-\d{2}-\d{2}$/.test(event.event_date)) {
    datesFragment = _allDayFragment(event.event_date, null);
  }
  if (datesFragment) parts.push("dates=" + datesFragment);
  if (event.location) {
    parts.push("location=" + encodeURIComponent(event.location));
  }
  parts.push("details=" + encodeURIComponent(calendarNotes(event)));
  return "https://calendar.google.com/calendar/render?" + parts.join("&");
};

// Precision helpers for the Option A `event_date` field.
// `event_start` is the precise ISO date-time; `event_date` is date-only
// (YYYY-MM-DD) used when the school gave a date but no clock time.
// Invariant enforced by backend: at most one populated. The UI reads
// event_start first; falls back to event_date when null.
export const eventDateIso = (event) =>
  event?.event_start || event?.event_date || null;

// Date-tile source hierarchy (2026-02):
//   1. concrete event_start
//   2. concrete event_date
//   3. active parent action's action_due_at — only when neither event
//      date is known (registration-only "Action Required" cards like
//      Gymnastics-team-registrations that otherwise show "TBD")
//   4. null → tile shows "TBD"
//
// When the tile is derived from a deadline (case 3) it renders a small
// "DUE" chip so the parent doesn't misread the deadline as the event
// date. The predicate is intentionally strict: only outstanding /
// in_progress / expired actions supply a deadline. Waiting actions
// (action_opens_at not yet reached) do not.
//
// SEMANTIC INVARIANT: this function is presentation-only. It NEVER
// writes action_due_at back into event_date on the canonical record.
const _DEADLINE_ELIGIBLE_ACTION_STATUSES = new Set([
  "outstanding",
  "in_progress",
  "expired",
]);
export const dateTileSource = (event) => {
  if (event?.event_start) return { iso: event.event_start, isDeadline: false };
  if (event?.event_date) return { iso: event.event_date, isDeadline: false };
  // Fallback: any active action with an action_due_at.
  for (const a of event?.actions || []) {
    if (!_DEADLINE_ELIGIBLE_ACTION_STATUSES.has(a?.status)) continue;
    if (a?.action_due_at) {
      return { iso: a.action_due_at, isDeadline: true };
    }
  }
  return { iso: null, isDeadline: false };
};

// --- Completed-status chip label (2026-02).
// The label reflects the type of action that was completed rather
// than collapsing everything to a generic "Actioned ✓". Purely
// presentational: does not alter action state or type.
const _COMPLETED_CHIP_LABELS = {
  BOOK: "Booked",
  ORDER: "Ordered",
  RSVP: "RSVP'd",
  REGISTER: "Registered",
  PAY: "Paid",
  REPLY: "Replied",
  CONSENT: "Consented",
  CHECK_BOOKING: "Checked",
};
export const completedActionChipLabel = (action) => {
  if (!action) return "Actioned";
  const type = String(action.action_type || "").toUpperCase();
  return _COMPLETED_CHIP_LABELS[type] || "Actioned";
};

export const eventHasKnownTime = (event) => Boolean(event?.event_start);

// All FrontRow events are Sydney-local by product definition (Trinity /
// PLC / etc are all Sydney schools). API payloads carry explicit
// +10:00/+11:00 offsets on datetime-with-time; date-only fields carry
// no offset. Rendering must lock to Australia/Sydney regardless of the
// browser's locale so that "2026-08-27T09:00:00+10:00" always reads as
// "Thu 27 Aug · 9:00 am" — never "Wed 26 Aug 11:00 pm" in a UTC browser.
export const APP_TIME_ZONE = "Australia/Sydney";

// Return true if the ISO looks like a date-only value (YYYY-MM-DD).
const _isDateOnly = (iso) => typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso);

// --- Date/time formatting for the canonical ISO 8601 fields.
export const formatDate = (iso) => {
  if (!iso) return { weekday: "", day: "", monthShort: "", full: "" };
  // Date-only strings (YYYY-MM-DD) are calendar dates with no
  // timezone: parse as LOCAL midnight to keep the numeric day stable
  // across browser locales. All-day events must NOT be shifted.
  if (_isDateOnly(iso)) {
    const [, y, m, day] = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const d = new Date(Number(y), Number(m) - 1, Number(day));
    if (Number.isNaN(d.getTime())) return { weekday: "", day: "", monthShort: "", full: "" };
    return {
      weekday: d.toLocaleDateString("en-AU", { weekday: "short" }),
      day: String(d.getDate()),
      monthShort: d.toLocaleDateString("en-AU", { month: "short" }),
      full: d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" }),
    };
  }
  // Datetime with explicit offset — pin rendering to Australia/Sydney.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { weekday: "", day: "", monthShort: "", full: "" };
  const tzDate = (opts) => d.toLocaleDateString("en-AU", { ...opts, timeZone: APP_TIME_ZONE });
  // getDate() uses browser-local time; use Intl parts pinned to Sydney.
  const dayNumParts = new Intl.DateTimeFormat("en-AU", { day: "numeric", timeZone: APP_TIME_ZONE }).format(d);
  return {
    weekday: tzDate({ weekday: "short" }),
    day: String(dayNumParts),
    monthShort: tzDate({ month: "short" }),
    full: tzDate({ weekday: "long", day: "numeric", month: "long" }),
  };
};

export const formatTime = (iso) => {
  if (!iso) return "";
  // A date-only string has no time — never fabricate 12:00 am.
  if (_isDateOnly(iso)) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d
    .toLocaleTimeString("en-AU", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: APP_TIME_ZONE,
    })
    .toLowerCase()
    .replace(/\s/g, " ");
};

export const formatRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const day = 24 * 60 * 60 * 1000;
  if (diffMs < 0) {
    // Future — used for opens/due dates. Pin to Sydney locale so a
    // 9am Sydney event doesn't render as the prior day in a UTC
    // browser.
    return d.toLocaleDateString("en-AU", {
      weekday: "short", day: "numeric", month: "short", timeZone: APP_TIME_ZONE,
    });
  }
  const diffDays = Math.floor(diffMs / day);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString("en-AU", {
    day: "numeric", month: "short", timeZone: APP_TIME_ZONE,
  });
};

// --- Display-layer composition helpers (do NOT mutate stored state).

// Strip a leading "{ChildName} · " (or •, -) from a Front Row-generated
// display_headline. Only strips when the prefixed name matches one of
// the event's canonical children — leaves the school's own event title
// intact when the child name is genuinely part of the school-supplied title.
export const eventHeading = (event, childrenMap = {}) => {
  const raw = event?.display_headline || event?.event_name || "";
  if (!raw) return "";
  // Strip a leading "{Name} · " prefix for ANY family child, not only
  // this event's currently-assigned children. This tolerates
  // reassignment: a prefix generated with an old child name is still
  // recognised as a Front Row-composed prefix and removed.
  const names = Object.values(childrenMap || {})
    .map((c) => c?.name)
    .filter(Boolean);
  for (const n of names) {
    const re = new RegExp(
      `^${n.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}\\s*[·•\\-]\\s+`
    );
    if (re.test(raw)) return raw.replace(re, "");
  }
  return raw;
};

// School-name suffix map for parent-facing location composition.
// Never invents an address — only appends recognised school context.
const _SCHOOL_LOCATION_SUFFIX = {
  "Trinity Grammar School": "Trinity Grammar School",
  "PLC Sydney": "PLC Sydney",
};

export const venueWithSchool = (event, childrenMap = {}) => {
  const venue = event?.location;
  if (!venue) return null;
  const firstChild = (event?.children || [])[0];
  const school = childrenMap[firstChild]?.school;
  const suffix = _SCHOOL_LOCATION_SUFFIX[school];
  if (!suffix) return venue;
  if (venue.toLowerCase().includes(suffix.toLowerCase())) return venue;
  return `${venue} · ${suffix}`;
};

// --- Update copy (shared by Recent Changes and Event Detail).
// Guarantees no raw ISO timestamps leak to the parent.
export const updateCopy = (update, event, childrenMap = {}) => {
  const eventTitle = eventHeading(event, childrenMap) || event?.event_name || "";
  const field = update?.field;
  const prev = update?.previous_value;
  const next = update?.new_value;
  const child = childrenMap[(event?.children || [])[0]]?.name || "Your child";
  const wasEmpty = prev == null || prev === "" || prev === "unknown";
  if (field === "arrival_time") {
    if (wasEmpty && next != null) {
      return { eventTitle, title: "Arrival time confirmed", body: `${child} needs to arrive at ${formatTime(next)}.` };
    }
    return { eventTitle, title: "Arrival time changed",
             body: `${child} needs to arrive at ${formatTime(next)} instead of ${formatTime(prev)}.` };
  }
  if (field === "event_start") {
    if (wasEmpty && next != null) {
      return { eventTitle, title: "Time confirmed", body: `${formatTime(next)}.` };
    }
    return { eventTitle, title: "Start time changed",
             body: `Now ${formatTime(next)} instead of ${formatTime(prev)}.` };
  }
  if (field === "event_end") {
    if (wasEmpty && next != null) {
      return { eventTitle, title: "End time confirmed", body: `${formatTime(next)}.` };
    }
    return { eventTitle, title: "End time changed",
             body: `Now ${formatTime(next)} instead of ${formatTime(prev)}.` };
  }
  if (field === "location") {
    if (wasEmpty && next) {
      return { eventTitle, title: "Location confirmed", body: `${next}.` };
    }
    return { eventTitle, title: "Location changed",
             body: `Now at ${next || "—"} instead of ${prev || "—"}.` };
  }
  if (field === "parent_attendance") {
    if (next === "yes") {
      return { eventTitle, title: "Attendance confirmed", body: "" };
    }
    if (next === "no") {
      return { eventTitle, title: "Attendance updated", body: "Not required." };
    }
    return { eventTitle, title: "Attendance updated", body: "" };
  }
  if (field === "event_date") {
    if (wasEmpty && next != null) {
      return { eventTitle, title: "Date confirmed", body: `${formatDate(next).full || next}.` };
    }
    return { eventTitle, title: "Date changed",
             body: `Now ${formatDate(next).full || next} instead of ${formatDate(prev).full || prev}.` };
  }
  // 2026-02-22 · Informational updates. When Brain emits an
  // `informational_updates` entry it lands here with
  // `classification === "informational"` and its own `update_type`.
  // Render the human copy from `headline` + `detail` directly.
  if (update?.classification === "informational") {
    const cleanDetail = String(update.detail || "")
      // Never show an Outlook SafeLinks tracking wrapper (including a
      // truncated wrapper) as customer-facing update copy.
      .replace(/https?:\/\/\S*safelinks\.protec\S*/gi, "")
      .replace(/\s+/g, " ")
      .trim();
    return {
      eventTitle,
      title: update.headline || INFORMATIONAL_UPDATE_TITLES[update.update_type] || "Update",
      body: cleanDetail,
      informational: true,
      update_type: update.update_type,
      source_url: update.source_url || null,
      effective_at: update.effective_at || null,
    };
  }
  return { eventTitle, title: update?.summary || "Update", body: "" };
};

// Section-header copy per informational update_type. Used when
// grouping updates on Event Detail into "Things to remember" vs
// "Useful info".
export const INFORMATIONAL_UPDATE_TITLES = {
  PREPARE:       "Something to prepare",
  LOGISTICS:     "On the day",
  CHANGE:        "Something changed",
  REMINDER:      "Reminder",
  RESOURCE_LINK: "Useful link",
  INFORMATION:   "Useful info",
};

// Grouping predicate: PREPARE / LOGISTICS / CHANGE surface under
// "Things to remember" (parent has to hold this in their head).
// REMINDER / RESOURCE_LINK / INFORMATION surface under "Useful info"
// (nice-to-know but no plan-item).
export const isPlanItemUpdate = (update) => {
  if (!update || update.classification !== "informational") return false;
  return ["PREPARE", "LOGISTICS", "CHANGE"].includes(update.update_type);
};

// --- Recent Changes: parent-explicit update resolution predicate.
// The dashboard hides an update as soon as ANY of the following are
// true:
//   * server-side ``acknowledged_at`` (parent already accepted)
//   * server-side ``dismissed_at``   (parent explicitly dismissed)
//   * local ``viewedUpdates[updateId]`` (optimistic hide before the
//     POST completes).
// Kept as a small pure helper so tests can lock the exact predicate
// without spinning up context / axios.
export const isUpdateResolvedForClient = (update, viewedUpdates = {}) => {
  if (!update) return true;
  if (update.acknowledged_at) return true;
  if (update.dismissed_at) return true;
  if (viewedUpdates[update.id]) return true;
  return false;
};


//
// Kept intentionally separate from Actions (see backend
// event_engine/fyi.py for the modelling rationale). FYI cards are
// newsletter receipts without article obligations. They never
// render an "Action required" chip and never
// contributes to completion evidence.
//
// The Brain / feed builder is authoritative for `is_fyi` and
// `fyi_state`. These client-side helpers exist so tests and hot
// components don't have to trust cache freshness — they can rederive
// on the fly from the same rules. Any drift between backend and
// frontend classification is a bug.
const _OBLIGATION_ACTION_TYPES_FYI = new Set([
  "BOOK", "ORDER", "RSVP", "REGISTER", "PAY", "CONSENT", "REPLY",
  "CHECK_BOOKING", "CHECK_DETAILS",
]);

// --- Urgency sorting for Needs Action (2026-02-15).
// Rank each Needs-Action event by its earliest relevant upcoming
// moment: min(earliest outstanding action_due_at, event_start /
// event_date). Undated → sentinel that sorts last.
const _URGENCY_UNDATED_SENTINEL = "9999-12-31T99:99";

export const urgencySortKey = (event) => {
  const candidates = [];
  if (event.event_start) candidates.push(String(event.event_start));
  else if (event.event_date) candidates.push(String(event.event_date));
  for (const a of event.actions || []) {
    const s = String(a.status || a.system_status || "").toLowerCase();
    if (s !== "outstanding" && s !== "waiting") continue;
    if (a.action_due_at) candidates.push(String(a.action_due_at));
  }
  if (candidates.length === 0) return _URGENCY_UNDATED_SENTINEL;
  candidates.sort();
  return candidates[0];
};

// Human-readable urgency reason. Returns null when undated.
export const urgencyReason = (event, now = new Date()) => {
  const isoKey = urgencySortKey(event);
  if (isoKey === _URGENCY_UNDATED_SENTINEL) return null;
  const evIso = event.event_start || event.event_date;
  const isEventKey = evIso && String(evIso) === isoKey;
  const dt = new Date(isoKey);
  if (Number.isNaN(dt.getTime())) return null;
  const startOfDay = (d) => { const c = new Date(d); c.setHours(0,0,0,0); return c; };
  const dayDiff = Math.round((startOfDay(dt) - startOfDay(now)) / 86400000);
  const hasTime = String(isoKey).includes("T");
  const timeStr = hasTime
    ? dt.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Australia/Sydney" }).toLowerCase().replace(" ", "")
    : null;
  if (dayDiff < 0) return isEventKey ? "Event passed" : "Overdue";
  if (dayDiff === 0) return isEventKey ? (timeStr ? `Today · ${timeStr}` : "Today") : (timeStr ? `Due today · ${timeStr}` : "Due today");
  if (dayDiff === 1) return isEventKey ? "Tomorrow" : "Due tomorrow";
  if (dayDiff < 7) {
    const dayName = dt.toLocaleDateString("en-AU", { weekday: "short", timeZone: "Australia/Sydney" });
    return isEventKey ? dayName : `Due ${dayName}`;
  }
  const opts = { weekday: "short", day: "numeric", month: "short", timeZone: "Australia/Sydney" };
  if (dt.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  const dateStr = dt.toLocaleDateString("en-AU", opts);
  return isEventKey ? dateStr : `Due ${dateStr}`;
};

// --- "New action required" — action arrived after a sibling
// completion. Uses field_history if present, otherwise falls back to
// action.created_at vs sibling.completed_at.
export const hasNewActionSinceCompletion = (event) => {
  if (!event || !event.actions || event.actions.length < 2) return false;
  let latestCompletedAt = null;
  const outstandings = [];
  for (const a of event.actions) {
    const s = String(a.status || a.system_status || "").toLowerCase();
    if (s === "completed" && a.completed_at) {
      const t = new Date(a.completed_at).getTime();
      if (!Number.isNaN(t) && (latestCompletedAt === null || t > latestCompletedAt)) latestCompletedAt = t;
    } else if (s === "outstanding") {
      outstandings.push(a);
    }
  }
  if (latestCompletedAt === null || outstandings.length === 0) return false;
  return outstandings.some((a) => {
    const created = a.created_at ? new Date(a.created_at).getTime() : NaN;
    if (Number.isNaN(created)) return false;
    return created > latestCompletedAt;
  });
};

// Prefer the Brain's stored `action_label` on the dashboard CTA when
// it's short and specific (e.g. "Order Sausage Sizzle Supper" beats
// generic "Order"). Falls back to the type-based compact label.
//
// Presentation-only strippers (2026-02): the dashboard CTA is a chip,
// not a full instruction — remove trailing " by <date>" and any
// trailing "via <mechanism>" / "on <system>" clauses so the button
// stays compact. The canonical `action.action_label` is untouched;
// View Details still shows the full label.
const _MAX_CARD_CTA_WORDS = 4;
const _TRAILING_VIA_ON_RE = /\s+(?:via|on|through|in)\s+.+$/i;
export const dashboardCtaLabel = (action, compactLabel) => {
  const raw = String(action?.action_label || "").trim();
  if (!raw) return compactLabel;
  // Strip trailing " by <date>" clause if present.
  let stripped = raw.replace(/\s+by\s+.+$/i, "").trim();
  // Strip trailing "via/on/through/in <mechanism>" clause so
  // "Provide consent via InSite" → "Provide consent".
  stripped = stripped.replace(_TRAILING_VIA_ON_RE, "").trim();
  const words = stripped.split(/\s+/);
  if (words.length === 0 || words.length > _MAX_CARD_CTA_WORDS) return compactLabel;
  return stripped;
};



export const isFyiEvent = (event) => {
  if (!event) return false;
  // Current feed owns classification; derive only for legacy payloads.
  if (typeof event.is_fyi === "boolean") return event.is_fyi;
  for (const a of event.actions || []) {
    const t = String(a.action_type || "").toUpperCase();
    if (_OBLIGATION_ACTION_TYPES_FYI.has(t)) return false;
  }
  return hasNewsletterReceiptIdentity(event);
};

// Returns "awaiting_ack" | "awaiting_calendar" | "resolved" | null.
// Prefer the server-provided `event.fyi_state` when present; fall
// back to a client derivation using the same rule as the backend.
export const fyiViewState = (event) => {
  if (!event) return null;
  if (event.fyi_state) return event.fyi_state;
  if (!isFyiEvent(event)) return null;
  if (!event.fyi_acknowledged_at) return "awaiting_ack";
  const hasDate = Boolean(event.event_start || event.event_end || event.event_date);
  if (!hasDate) return "resolved";
  const decision = String(event.calendar_decision || "eligible").toLowerCase();
  if (decision !== "eligible") return "resolved";
  return "awaiting_calendar";
};

// --- Inline Add-to-calendar CTA eligibility.
// Show the pill on ordinary Coming Up cards (and resolved-action
// cards, which live in Coming Up too) when:
//   * the event has a usable date (event_start or event_date), AND
//   * the parent hasn't yet decided (`calendar_decision === 'eligible'`
//     or missing).
// Never uses `action_due_at` as a substitute for an event date — that
// is a deadline and would mislead the parent.
export const showInlineCalendarCta = (event) => {
  if (!event) return false;
  if (!(event.event_start || event.event_date)) return false;
  const decision = String(event.calendar_decision || "eligible").toLowerCase();
  return decision === "eligible";
};


// Handled = parent actually resolved something.
// - Zero actions → no chip (event is purely informational).
// - Any completed action → "handled" (completed wins over expired: a
//   later deadline expiring on a sibling action MUST NOT overwrite the
//   completed state).
// - Otherwise, any expired unresolved action → "deadline_passed"
//   (deadline passed but the event itself is still in the future;
//   parent may still add to calendar or dismiss).
// - All actions resolved AND ≥1 action existed → "handled".
// - Any outstanding action → this event isn't in Coming Up at all.
export const comingUpChip = (event) => {
  const acts = event?.actions || [];
  if (acts.length === 0) return null;
  if (deferredActions(event).length > 0) return "deferred";
  if (acts.some((a) => a.status === "completed")) return "handled";
  if (hasExpiredAction(event)) return "deadline_passed";
  return allActionsResolved(event) ? "handled" : null;
};


// Action-type-specific label for the `deadline_passed` chip. Picks
// the expired action whose type drives the wording:
//   BOOK       → "Booking closed"
//   REGISTER   → "Registration closed"
//   RSVP       → "RSVP closed"
//   otherwise  → undefined (StatusChip falls back to "Deadline passed")
export const expiredActionClosedLabel = (event) => {
  const expired = (event?.actions || []).find((a) => a.status === "expired");
  if (!expired) return undefined;
  const type = String(expired.action_type || "").toUpperCase();
  if (type === "BOOK") return "Booking closed";
  if (type === "REGISTER") return "Registration closed";
  if (type === "RSVP") return "RSVP closed";
  return undefined;
};



// --- Action selection helpers.
// `in_progress` = parent opened the external action link but has not yet
// confirmed completion. Treated as still-outstanding so the event stays
// in the "Needs action" list and the calendar prompt is NOT triggered.
//
// 2026-08-31 · closure release: an action the parent has explicitly
// dismissed (server-persisted `parent_status === "dismissed"`) is
// excluded from every attention selector so the card stays absent
// across refetch, refresh, sign-out/sign-in and polling.
const isDismissedByParent = (a) =>
  String(a?.parent_status || "").toLowerCase() === "dismissed";

export const outstandingActions = (event) =>
  (event.actions || []).filter(
    (a) =>
      (a.status === "outstanding" || a.status === "in_progress") &&
      !isDismissedByParent(a)
  );

export const waitingActions = (event) =>
  (event.actions || []).filter((a) => a.status === "waiting");

// Later defers an unfinished task. It is never completion evidence.
export const deferredActions = (event) =>
  (event?.actions || []).filter((a) => a.status === "snoozed" && !isDismissedByParent(a));

// Later suppresses the information already present at that choice, not a
// genuinely newer school communication. Never acknowledge the stored update.
export const isUpdateDeferred = (event, update) => {
  const deferred = deferredActions(event);
  if (!deferred.length) return false;
  const received = Date.parse(update?.received_at || "");
  return deferred.some(action => {
    const saved = Date.parse(action.deferred_at || "");
    return !Number.isFinite(received) || !Number.isFinite(saved) || received <= saved;
  });
};

export const allActionsResolved = (event) => {
  const acts = event.actions || [];
  if (acts.length === 0) return true;
  return acts.every(
    (a) =>
      ["completed", "not_for_us"].includes(a.status) ||
      isDismissedByParent(a)
  );
};

// Expired-but-unresolved: the deadline has passed but the parent has
// neither completed nor dismissed the action. Treat as still-relevant
// for classification/attention, but with distinct chip semantics.
export const hasExpiredAction = (event) =>
  (event.actions || []).some(
    (a) => a.status === "expired" && !isDismissedByParent(a)
  );

// 2026-08-31 · closure release: ambiguous records the system holds
// for OPERATOR review (unresolved pending parent inputs, e.g. "Which
// child is this for?"). Parents are never asked — held records are
// preserved server-side but excluded from every parent-facing lane
// and count.
export const isHeldForOperatorReview = (event) =>
  Array.isArray(event?.pending_inputs) && event.pending_inputs.length > 0;

// --- Time-based dashboard filtering ---------------------------------------
//
// Mirrors the server-side `is_past_event` predicate in feed_builder.py.
// Belt-and-braces: even with a stale cached feed, the client refuses
// to render past events on the active dashboard.
//
// Rules (approved 2026-02):
//   * event_end known                → past iff now > event_end.
//   * event_start known, no end      → past iff now > event_start.
//   * event_date only                → past iff school-local today >
//                                       event_date (survives entire
//                                       local calendar day).
//   * Undated                        → NOT past.
//
// `event_date` is a school-local calendar date without a tz. Without
// a per-school tz field yet, we hardcode the prototype school's tz
// (Australia/Sydney) so a browser running in UTC (e.g. CI containers)
// doesn't silently drift the boundary by 10+ hours. This mirrors the
// server-side default. TODO: read `school_tz` from the feed payload
// once the backend exposes it.
const _SCHOOL_TZ = "Australia/Sydney";
const _schoolTodayIso = (nowMs) => {
  try {
    // en-CA yields "YYYY-MM-DD" natively.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: _SCHOOL_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(nowMs));
  } catch {
    // Fallback if the runtime lacks the tz db.
    const d = new Date(nowMs);
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  }
};

// Project an ISO timestamp to the school-local wall-clock components
// (year/month/day/hour/minute/second) via Intl. Returns null if the
// input is unparseable. Used by the midnight-anchor detection below.
const _schoolLocalParts = (isoStr) => {
  const t = Date.parse(isoStr);
  if (Number.isNaN(t)) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: _SCHOOL_TZ,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    }).formatToParts(new Date(t));
    const g = (name) => parts.find((p) => p.type === name)?.value;
    // en-CA hour uses "24" for midnight; normalise to "00".
    const hh = g("hour") === "24" ? "00" : g("hour");
    return {
      dateIso: `${g("year")}-${g("month")}-${g("day")}`,
      hour: Number(hh),
      minute: Number(g("minute")),
      second: Number(g("second")),
    };
  } catch {
    return null;
  }
};

export const isPastEvent = (event, nowMs = Date.now()) => {
  if (!event) return false;
  const _asUtc = (iso) => {
    if (typeof iso !== "string" || !iso) return NaN;
    const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/.test(iso.trim());
    return Date.parse(hasOffset ? iso : `${iso}Z`);
  };
  if (event.event_end) {
    const t = _asUtc(event.event_end);
    if (!Number.isNaN(t)) return nowMs > t;
  }
  if (event.event_start) {
    // 2026-02 (Book Week / Author Visit): local-midnight event_start
    // with no end is a DATE ANCHOR, not a genuine 00:00 timed event.
    // Apply the calendar-day rule against its Sydney-local date so
    // the event survives its full local day. Any non-midnight local
    // time falls through to the timed compare below unchanged.
    if (!event.event_end) {
      const parts = _schoolLocalParts(event.event_start);
      if (parts && parts.hour === 0 && parts.minute === 0 && parts.second === 0) {
        return _schoolTodayIso(nowMs) > parts.dateIso;
      }
    }
    const t = _asUtc(event.event_start);
    if (!Number.isNaN(t)) return nowMs > t;
  }
  if (event.event_date) {
    // Lexical ISO-date comparison is safe: "YYYY-MM-DD" strings sort
    // the same as calendar dates.
    return _schoolTodayIso(nowMs) > event.event_date;
  }
  return false;
};

// --- Concluded informational / event-only filter (2026-08-30) ------------
//
// After the Sydney-local event end (or all-day date) passes, purely
// informational or event-only cards must disappear from the
// dashboard's bottom / recent / past area. Booked / RSVP'd / paid /
// consented events — anything with completed evidence — remain
// accessible via Event Detail (this predicate is display-only, no
// canonical / evidence is deleted).
//
// A concluded event-only card is one where:
//   • ``isPastEvent(event, nowMs)`` is true (Sydney date/time based
//     via the same helper the whole app uses), AND
//   • the event carries no completed evidence — no completed_at
//     transactional action (BOOK / RSVP / CONSENT / PAY / REGISTER /
//     ORDER / REPLY), AND
//   • no OUTSTANDING transactional action either (those already
//     surface in the "past actions to review" section).
//
// Future booked events are unaffected because ``isPastEvent`` is
// false for them. Past events WITH completed evidence are
// unaffected because they carry a completed transactional action.
const _EVIDENCE_TYPES = new Set([
  "BOOK", "ORDER", "RSVP", "REGISTER", "PAY", "CONSENT", "REPLY",
]);

export const isConcludedInformationalOnly = (event, nowMs = Date.now()) => {
  if (!event) return false;
  if (!isPastEvent(event, nowMs)) return false;
  const actions = Array.isArray(event.actions) ? event.actions : [];
  for (const a of actions) {
    const t = String(a?.action_type || "").toUpperCase();
    if (!_EVIDENCE_TYPES.has(t)) continue;
    // Any completed evidence → keep the event accessible (Event
    // Detail history renders it). Outstanding evidence → surfaced
    // in the "past actions to review" lane, not hidden here.
    if (a.completed_at) return false;
    const sys = String(a.system_status || a.status || "").toLowerCase();
    const par = String(a.parent_status || "").toLowerCase();
    if (par === "done" || sys === "done") return false;
    if (sys === "outstanding" || sys === "in_progress") return false;
  }
  return true;
};

// --- Manual resolution eligibility (Event Detail).
// Book / Order / RSVP / Register / Pay are transactional actions the
// parent completes off-app (school portal, payment page, etc.). On the
// Event Detail we surface an explicit "I've done this" / "Not done
// yet" pair for these so the parent can resolve them without first
// clicking the external link. CHECK_BOOKING (scaffolding for
// uncertainty on parent-attendance events) is included: once the
// parent has viewed the notice they may explicitly resolve it too.
// Non-transactional or informational actions do NOT get the pair.
const _RESOLVABLE_TYPES = new Set([
  "BOOK", "ORDER", "RSVP", "REGISTER", "PAY", "CONSENT", "REPLY",
  "CHECK_BOOKING", "CHECK_DETAILS",
]);
export const isManuallyResolvableAction = (action) => {
  if (!action) return false;
  const type = String(action.action_type || "").toUpperCase();
  if (!_RESOLVABLE_TYPES.has(type)) return false;
  return action.status === "outstanding" || action.status === "in_progress";
};

// CHECK_BOOKING is the deterministic uncertainty action emitted by
// the backend when a parent-attendance event lacks explicit booking
// details in the source. Renders with a distinct "Check booking" chip
// and CTA copy "View school notice" (see NeedsActionCard / EventDetail).
export const isCheckBookingAction = (action) => {
  if (!action) return false;
  return String(action.action_type || "").toUpperCase() === "CHECK_BOOKING";
};

// --- Server-vs-client action-status precedence.
// The backend is authoritative for TERMINAL statuses (`completed`,
// `expired`) — those reflect real deterministic evidence (ticket
// email, deadline passed) and must win over any local optimistic
// state so genuine completion evidence resolves the card without
// requiring the parent to also tap "I've done this".
//
// For non-terminal statuses (`outstanding`, `waiting`) the client
// override is respected — that carries the parent's local UI state
// (`in_progress` after opening the external link, an optimistic
// `completed`/`snoozed`/`not_for_us` set by the parent) which is not
// yet persisted on the server.
const _SERVER_TERMINAL_STATUSES = new Set(["completed", "expired"]);
export const resolveActionStatus = (serverStatus, clientStatus, personalDeferred) => {
  if (serverStatus && _SERVER_TERMINAL_STATUSES.has(serverStatus)) {
    return serverStatus;
  }
  const localStatus = typeof clientStatus === "object" ? clientStatus?.status : clientStatus;
  // A saved Do now on another device supersedes an old browser-only Later.
  // Engagement and optimistic completion remain distinct local transitions.
  if (personalDeferred !== undefined && localStatus === "snoozed") return serverStatus;
  return localStatus || serverStatus;
};

export const primaryAction = (event) => {
  const outstanding = outstandingActions(event);
  if (outstanding.length > 0) {
    // 2026-03 — CHECK_BOOKING is the deterministic uncertainty
    // placeholder synthesized when we cannot confirm from the source
    // whether the parent still needs to act. Whenever a MORE SPECIFIC
    // outstanding action (BOOK, RSVP, CONSENT, PAY, REGISTER, ORDER,
    // REPLY) is present on the same event, CHECK_BOOKING must NOT
    // outrank it as the dashboard's primary CTA — the specific action
    // is what the parent actually needs to do. Preserves creation
    // order among specific actions (unchanged) and among multiple
    // CHECK_BOOKING placeholders (unchanged).
    // 2026-08-29: CHECK_DETAILS is even softer than CHECK_BOOKING
    // (informational uncertainty only). The same rule applies —
    // any real obligation OR CHECK_BOOKING outranks CHECK_DETAILS.
    const SOFT_UNCERTAINTY_TYPES = new Set(["CHECK_BOOKING", "CHECK_DETAILS"]);
    const specific = outstanding.filter(
      (a) => !SOFT_UNCERTAINTY_TYPES.has(String(a.action_type || "").toUpperCase())
    );
    if (specific.length > 0) return specific[0];
    // Among soft-uncertainty actions, CHECK_BOOKING wins over
    // CHECK_DETAILS (evidence-backed vs informational).
    const checkBooking = outstanding.filter(
      (a) => String(a.action_type || "").toUpperCase() === "CHECK_BOOKING"
    );
    if (checkBooking.length > 0) return checkBooking[0];
    return outstanding[0];
  }
  const waiting = waitingActions(event);
  if (waiting.length > 0) return waiting[0];
  return (event.actions || [])[0] || null;
};

export const actionLabel = (action) => {
  if (!action) return "";
  if (action.action_label) return action.action_label;
  return ACTION_TYPE_DEFAULTS[action.action_type]?.fallbackLabel || "Mark done";
};

// Simple parent-facing verb for the CTA. This is display only —
// action_label / action_url / action_type on the underlying record
// are unchanged.
//
// Mapping (checked in order — first match wins):
//   Order   – food, merchandise, orders (supper, morning tea, canteen, uniform, purchase)
//   Pay     – payments / invoices / fees
//   RSVP    – RSVP-only asks
//   Register – registrations, sign-ups
//   Book    – bookings, tickets, seats, reservations
//   View    – informational destination (default fallback)
export const compactActionLabel = (action) => {
  if (!action) return "View";
  const label = (action.action_label || "").toLowerCase();
  const type = (action.action_type || "").toUpperCase();

  // CHECK_DETAILS — informational-uncertainty fallback (2026-08-29).
  // The source directs the parent to a linked notice/schedule/…
  // FrontRow could not fetch. Fixed CTA copy: "View school notice".
  if (type === "CHECK_DETAILS") {
    return "View school notice";
  }

  // CHECK_BOOKING — soft uncertainty scaffolding.
  // General fallback rule (2026-02):
  //   * If we know the source portal (InSite, Compass, TryBooking…)
  //     → "Check <Portal>" — tells the parent exactly where to look.
  //   * If we have a usable action_url but no portal name
  //     → "View school notice".
  //   * Otherwise → "Check booking" (same phrase as the chip).
  if (type === "CHECK_BOOKING") {
    const portal = (action.action_portal || "").trim();
    if (portal) return `Check ${portal}`;
    const url = action.action_url;
    if (typeof url === "string" && url.length > 0 && url !== "#") {
      return "View school notice";
    }
    // Fallback (2026-02-18) — the previous "Check details" copy
    // disagreed with the "Check booking" status chip on the dashboard
    // card. Both must reflect the same underlying uncertainty: the
    // parent should verify booking status. Keep the CTA phrase
    // aligned with the chip so the card can't render a contradictory
    // pair (chip="Check booking" + CTA="Check details").
    return "Check booking";
  }

  // CONSENT — parent-facing verb is "Provide consent".
  if (type === "CONSENT" || /consent/.test(label)) {
    return "Provide consent";
  }

  // Order: food / merchandise / general "order …"
  if (
    /(?:^|\W)order(?:\W|$)/i.test(label) ||
    /morning tea|supper|canteen|uniform|merchandise|purchase|buy/.test(label)
  ) {
    return "Order";
  }
  // Pay
  if (type === "PAY" || /(?:^|\W)(?:pay|payment|invoice|fee)(?:\W|$)/i.test(label)) {
    return "Pay";
  }
  // RSVP
  if (/(?:^|\W)rsvp(?:\W|$)/i.test(label)) {
    return "RSVP";
  }
  // Register
  if (type === "REGISTER" || /register|registration|sign up|sign-up|enrol/.test(label)) {
    return "Register";
  }
  // Book — tickets, seats, reservations
  if (
    type === "BOOK" ||
    /book|ticket|seat|reserve|reservation/.test(label)
  ) {
    return "Book";
  }
  return "View";
};

export const actionDoneLabel = (action) => {
  if (!action) return "Done";
  return ACTION_TYPE_DEFAULTS[action.action_type]?.doneLabel || "Done";
};

export const actionDoneToast = (action) => {
  if (!action) return "Marked done";
  return ACTION_TYPE_DEFAULTS[action.action_type]?.doneToast || "Marked done";
};

// --- Event status → chip mapping.
export const eventChipStatus = (event, { onCalendar } = {}) => {
  const acts = event.actions || [];
  const outstanding = outstandingActions(event);
  if (outstanding.length > 0) {
    // Soft uncertainty precedence: if EVERY outstanding action is a
    // soft-uncertainty scaffold (CHECK_BOOKING or CHECK_DETAILS),
    // render the softer amber chip so the parent isn't nagged about
    // something that may turn out to be walk-in / purely
    // informational. As soon as ANY hard action (BOOK/RSVP/…) is
    // also outstanding, the hard `action_required` chip wins.
    //
    // Among soft-uncertainty actions themselves, CHECK_BOOKING
    // (evidence-backed booking uncertainty) beats CHECK_DETAILS
    // (purely informational uncertainty) — both render the same
    // amber palette but with distinct chip labels.
    const SOFT = new Set(["CHECK_BOOKING", "CHECK_DETAILS"]);
    const allSoft = outstanding.every((a) =>
      SOFT.has(String(a.action_type || "").toUpperCase())
    );
    if (!allSoft) return "action_required";
    const anyCheckBooking = outstanding.some((a) =>
      String(a.action_type || "").toUpperCase() === "CHECK_BOOKING"
    );
    return anyCheckBooking ? "check_booking" : "check_details";
  }
  // Completed wins over expired: if the parent completed something,
  // a sibling action expiring later must NOT downgrade the event
  // to "Deadline passed".
  if (deferredActions(event).length > 0) return "deferred";
  const hasCompleted = acts.some((a) => a.status === "completed");
  if (hasCompleted) {
    const completedBooking = acts.some((a) => a.status === "completed"
      && ["BOOK", "RSVP", "REGISTER", "ORDER", "PAY", "CONSENT"].includes(a.action_type));
    return onCalendar ? "coming_up" : completedBooking ? "booked" : "handled";
  }
  // Expired-but-unresolved: deadline passed and nothing completed.
  // Distinct from `handled` — the parent may still add to calendar
  // or dismiss.
  if (acts.some((a) => a.status === "expired")) return "deadline_passed";
  const waiting = waitingActions(event);
  if (waiting.length > 0) return "coming_up";
  if (allActionsResolved(event) && acts.length > 0) {
    return onCalendar ? "coming_up" : "handled";
  }
  return "coming_up";
};


// --- Status-derived visual accents for the Event Detail view (2026-02-18).
//
// The dashboard card resolves its palette from ``eventChipStatus`` via
// ``StatusChip``. The Event Detail view had been hardcoded to a
// forest-green brand accent for the DetailRow icons and the primary
// CTA, so an Action-Required or Check-Booking event was rendering
// predominantly green in the details view. Both surfaces must derive
// visual state from a single source of truth.
//
// This helper returns Tailwind class strings for the accent palette
// that mirrors ``StatusChip`` — same six chip states, same colours.
// Use for the DetailRow icon (``iconFg`` + ``iconBg``) and the primary
// CTA button (``ctaBg`` + ``ctaHover``) so the details page keeps
// consistent with whichever chip the parent saw on the dashboard.
//
// Falls back to ``coming_up`` styling for unknown / null status.
const _STATUS_ACCENT = {
  action_required: {
    iconFg: "text-[#8A3E20]",
    iconBg: "bg-[#FBEDE5]",
    ctaBg: "bg-[#8A3E20]",
    ctaHover: "hover:bg-[#6E301A]",
  },
  check_booking: {
    iconFg: "text-[#6B5116]",
    iconBg: "bg-[#FBF3DF]",
    ctaBg: "bg-[#6B5116]",
    ctaHover: "hover:bg-[#553F10]",
  },
  // Informational uncertainty (2026-08-29) — mirrors check_booking
  // palette; different chip label but same amber accent so the
  // parent recognises "not-quite-action-required" at a glance.
  check_details: {
    iconFg: "text-[#6B5116]",
    iconBg: "bg-[#FBF3DF]",
    ctaBg: "bg-[#6B5116]",
    ctaHover: "hover:bg-[#553F10]",
  },
  deadline_passed: {
    iconFg: "text-[#6B5116]",
    iconBg: "bg-[#FBF3DF]",
    ctaBg: "bg-[#6B5116]",
    ctaHover: "hover:bg-[#553F10]",
  },
  booked: {
    iconFg: "text-[#2F5C3E]",
    iconBg: "bg-[#E7EFE7]",
    ctaBg: "bg-[#4A5D4E]",
    ctaHover: "hover:bg-[#3E4F42]",
  },
  handled: {
    iconFg: "text-[#2F5C3E]",
    iconBg: "bg-[#E7EFE7]",
    ctaBg: "bg-[#4A5D4E]",
    ctaHover: "hover:bg-[#3E4F42]",
  },
  coming_up: {
    iconFg: "text-[#1A365D]",
    iconBg: "bg-[#EEF2F7]",
    ctaBg: "bg-[#1A365D]",
    ctaHover: "hover:bg-[#0F2340]",
  },
  complete: {
    iconFg: "text-[#5C625E]",
    iconBg: "bg-[#F5F5F2]",
    ctaBg: "bg-[#5C625E]",
    ctaHover: "hover:bg-[#4A4F4C]",
  },
};

export const statusAccent = (status) =>
  _STATUS_ACCENT[status] || _STATUS_ACCENT.coming_up;

